/**
 * Account changes the dApp token can no longer make (tss-api #6, D4): the parent keeps the UI and asks the
 * iframe to send the request with the iframe session. Only the routes in account-routes are accepted.
 *
 * Linking: TSS answers a link from the iframe session without tokens (the session keeps its one refresh chain).
 * Tokens in a link answer are removed anyway, so none reaches the parent; `providers` updates the stored basics.
 * Passkey linking runs the ceremony in the parent page, and the X popup posts its result to that page only, so for
 * both the iframe sends that page's origin as `parent_origin`.
 */

import { isLinkRoute, parseAccountRoute } from '../../lib/account-routes'
import { AccountAbstractionError, SessionExpiredError } from '../../lib/errors'
import { iframeSession, type IframeSession } from './iframe-session'
import { sdkVersionHeaders } from './sdk-version'

declare const __EMBARK_TSS_URL__: string

export interface AccountRequestResult {
  ok: boolean
  status: number
  data: unknown
}

function defaultTssUrl(): string {
  return (typeof __EMBARK_TSS_URL__ !== 'undefined' && __EMBARK_TSS_URL__) || 'https://api.embarkai.io/tss'
}

/** Routes that take the dApp page origin (`parent_origin`): the passkey ceremony and the X popup run there */
const PARENT_ORIGIN_ROUTES = new Set(['/api/auth/link/passkey/begin', '/api/auth/link/x/start'])

export async function sendAccountRequest(
  request: { method?: unknown; path?: unknown; body?: unknown },
  projectId: string,
  session: IframeSession = iframeSession,
  tssUrl: () => string = defaultTssUrl,
  parentOrigin?: string
): Promise<AccountRequestResult> {
  const method = typeof request?.method === 'string' ? request.method.toUpperCase() : ''
  const route = parseAccountRoute(method, typeof request?.path === 'string' ? request.path : '')
  if (!route) {
    throw new AccountAbstractionError(`Not an account request: ${method} ${String(request?.path)}`, 'INVALID_REQUEST')
  }
  if (!session.has()) throw new SessionExpiredError('No iframe session, please sign in')

  let body = request.body
  if (PARENT_ORIGIN_ROUTES.has(route.path) && parentOrigin) {
    body = { ...(body && typeof body === 'object' ? body : {}), parent_origin: parentOrigin }
  }

  const query = new URLSearchParams(route.query)
  query.set('projectId', projectId)
  const url = `${tssUrl()}${route.path}?${query.toString()}`
  const send = (token: string) =>
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...sdkVersionHeaders() },
      ...(body !== undefined && method !== 'DELETE' ? { body: JSON.stringify(body) } : {})
    })

  let response = await send(await session.getAccessToken())
  if (response.status === 401) {
    const refreshed = await session.refresh()
    if (!refreshed.token) {
      if (refreshed.sessionDead) throw new SessionExpiredError()
      throw new Error('Token refresh temporarily unavailable, please retry')
    }
    response = await send(refreshed.token)
  }

  let data = (await response.json().catch(() => null)) as Record<string, unknown> | null
  if (response.ok && data && typeof data === 'object' && isLinkRoute(route.path)) {
    data = keepLinkTokens(data, session)
  }
  return { ok: response.ok, status: response.status, data }
}

/**
 * No link tokens reach the parent. TSS issues none to the iframe session; the session is never replaced from a
 * link answer, so it keeps its one refresh chain. `providers` updates the stored basics.
 */
function keepLinkTokens(data: Record<string, unknown>, session: IframeSession): Record<string, unknown> {
  const { accessToken: _accessToken, refreshToken: _refreshToken, expiresIn: _expiresIn, ...rest } = data
  if (Array.isArray(rest.providers)) session.update({ providers: rest.providers as string[] })
  return rest
}
